/**
 * Utility functions for handling localized footer links
 */

import { toBcp47 } from '@ophelos/shared/utils/languages';

export interface FooterLink {
  name: string | Record<string, string>; // String (backward compatible) or localized object
  url: string;
}

/**
 * Map a language code — a short ISO code (`fr`) or an already-BCP 47 tag
 * (`fr-FR`) — to BCP 47 locale format. Unknown or unset codes resolve to the
 * neutral English default (matching `theme.schema.json`), never Portuguese.
 */
export const mapLanguageToLocale = (lang: string): string => toBcp47(lang);

/**
 * Get localized name from a footer link name field
 * 
 * @param name - Name field (string or localized object)
 * @param currentLanguage - Current language code (e.g., 'en', 'pt')
 * @param defaultLocale - Default locale from config (e.g., 'en-GB', 'pt-PT')
 * @param fallbackLocale - Fallback locale from config (e.g., 'en-GB', 'pt-PT')
 * @returns Localized name string
 */
function getLocalizedName(
  name: string | Record<string, string>,
  currentLanguage: string,
  defaultLocale?: string,
  fallbackLocale?: string
): string {
  // If it's a simple string, return it directly (backward compatible)
  if (typeof name === 'string') {
    return name;
  }

  // If it's a localized object, select the appropriate locale
  const currentLocale = mapLanguageToLocale(currentLanguage);
  
  // Try current locale first
  if (name[currentLocale]) {
    return name[currentLocale];
  }

  // Try default locale
  if (defaultLocale && name[defaultLocale]) {
    return name[defaultLocale];
  }

  // Try fallback locale
  if (fallbackLocale && name[fallbackLocale]) {
    return name[fallbackLocale];
  }

  // Try any available locale as last resort
  const availableLocales = Object.keys(name);
  if (availableLocales.length > 0) {
    return name[availableLocales[0]];
  }

  // Last resort: return empty string
  return '';
}

/**
 * Get localized footer links based on current language
 * 
 * The name field can be:
 * - A simple string (backward compatible): {name: "Secure", url: "#"}
 * - A localized object: {name: {"en-GB": "Secure", "pt-PT": "Seguro"}, url: "#"}
 * 
 * The url field is always a simple string (same for all locales).
 * 
 * @param footerLinks - Footer links array
 * @param currentLanguage - Current language code (e.g., 'en', 'pt')
 * @param defaultLocale - Default locale from config (e.g., 'en-GB', 'pt-PT')
 * @param fallbackLocale - Fallback locale from config (e.g., 'en-GB', 'pt-PT')
 * @returns Array of footer links with localized names
 */
export function getLocalizedFooterLinks(
  footerLinks: FooterLink[] | undefined,
  currentLanguage: string,
  defaultLocale?: string,
  fallbackLocale?: string
): Array<{ name: string; url: string }> {
  if (!footerLinks || !Array.isArray(footerLinks)) {
    return [];
  }

  return footerLinks.map((link) => ({
    name: getLocalizedName(link.name, currentLanguage, defaultLocale, fallbackLocale),
    url: link.url,
  })).filter((link) => link.name); // Filter out links with empty names
}

